import { useState } from 'react';
import { prepareUpload, finalizeUpload } from '../services/DocumentationService';
import { validateDocumentFile } from '../utils/fileUploadValidation';

interface UploadFile {
  id: string;
  file: File;
}

interface UseDocumentUploadOptions {
  aiSystemId: string;
  category?: string;
  pathPrefix?: string;
  conformityAssessmentType?: 'INTERNAL' | 'EXTERNAL';
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useDocumentUpload = ({
  aiSystemId,
  category: defaultCategory,
  pathPrefix = 'user-uploads',
  conformityAssessmentType: defaultConformityAssessmentType,
  onSuccess,
  onError,
}: UseDocumentUploadOptions) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<Record<string, number>>({});

  const uploadSingleFile = async (id: string, file: File, category: string, contentType: string, conformityAssessmentType?: 'INTERNAL' | 'EXTERNAL') => {
    setProgress((prev) => ({ ...prev, [id]: 10 }));

    // Step 1: Prepare upload
    const prepareResponse = await prepareUpload({
      aiSystemId,
      fileName: file.name,
      contentType,
      fileSize: file.size,
      category,
      pathPrefix,
      conformityAssessmentType,
    });

    const { uploadId, upload } = prepareResponse;
    setProgress((prev) => ({ ...prev, [id]: 30 }));

    // Step 2: Upload to S3
    const s3Response = await fetch(upload.url, {
      method: upload.method || 'PUT',
      headers: upload.headers || {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!s3Response.ok) {
      throw new Error(`S3 upload failed: ${s3Response.statusText}`);
    }

    const etag = s3Response.headers.get('ETag');
    setProgress((prev) => ({ ...prev, [id]: 70 }));

    // Step 3: Finalize upload — returns the created/updated Document record.
    const document = await finalizeUpload({
      uploadId,
      etag: etag || undefined,
    });

    setProgress((prev) => ({ ...prev, [id]: 100 }));

    return document.id;
  };

  const uploadFiles = async (files: UploadFile[], category?: string, conformityAssessmentType?: 'INTERNAL' | 'EXTERNAL') => {
    setUploading(true);
    const results: Array<{ success: boolean; file: File; error?: string; documentId?: string }> = [];
    const uploadCategory = category || defaultCategory || 'OTHER';
    const assessmentType = conformityAssessmentType || defaultConformityAssessmentType;

    try {
      for (const { id, file } of files) {
        // Last-resort client guard mirroring the backend contract, so an
        // invalid file never reaches the API (which would 403).
        const validation = validateDocumentFile(file);
        if (!validation.valid) {
          results.push({ success: false, file, error: validation.message });
          setProgress((prev) => ({ ...prev, [id]: 0 }));
          continue;
        }

        try {
          const documentId = await uploadSingleFile(id, file, uploadCategory, validation.mimeType, assessmentType);
          results.push({ success: true, file, documentId });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          results.push({ success: false, file, error: errorMessage });
          setProgress((prev) => ({ ...prev, [id]: 0 }));
        }
      }

      const allSuccess = results.every((r) => r.success);
      if (allSuccess && onSuccess) {
        onSuccess();
      } else if (!allSuccess && onError) {
        const failedFiles = results.filter((r) => !r.success);
        // Surface the real per-file reasons so callers can show a useful
        // message, falling back to a count when no reason is available.
        const message =
          failedFiles
            .map((r) => r.error)
            .filter(Boolean)
            .join('; ') || `${failedFiles.length} file(s) failed to upload`;
        onError(new Error(message));
      }

      return results;
    } catch (error) {
      if (onError && error instanceof Error) {
        onError(error);
      }
      return results;
    } finally {
      setUploading(false);
      setProgress({});
    }
  };

  return {
    uploadFiles,
    uploading,
    progress,
  };
};
